import { apiRequest, apiUpload } from './api'
import {
  User,
  LoginCredentials,
  RegisterFamilyData,
  AuthResponse,
  LoginResponse,
  TwoFactorStatus,
  TwoFactorSetupResponse,
  TwoFactorEnableResponse,
  VerifyTwoFactorParams,
} from '../types'

export const authService = {
  // Login user — returns a challenge token instead of an access token when
  // the account has two-factor authentication enabled.
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await apiRequest.post<LoginResponse>('/auth/login', credentials)
    return response.data
  },

  // Get two-factor status for the current user
  async getTwoFactorStatus(): Promise<TwoFactorStatus> {
    const response = await apiRequest.get<TwoFactorStatus>('/auth/2fa/status')
    return response.data
  },

  // Start enrollment: returns otpauth URL (for the QR) + secret for manual entry
  async setupTwoFactor(): Promise<TwoFactorSetupResponse> {
    const response = await apiRequest.post<TwoFactorSetupResponse>('/auth/2fa/setup')
    return response.data
  },

  // Confirm enrollment — recovery codes are returned exactly once
  async enableTwoFactor(code: string): Promise<TwoFactorEnableResponse> {
    const response = await apiRequest.post<TwoFactorEnableResponse>('/auth/2fa/enable', { code })
    return response.data
  },

  // Disable (requires current password + TOTP or recovery code)
  async disableTwoFactor(password: string, code: string): Promise<{ message: string }> {
    const response = await apiRequest.post<{ message: string }>('/auth/2fa/disable', { password, code })
    return response.data
  },

  // Exchange a login challenge token for an access token
  async verifyTwoFactor(params: VerifyTwoFactorParams): Promise<AuthResponse> {
    const response = await apiRequest.post<AuthResponse>('/auth/2fa/verify', params)
    return response.data
  },

  // Register family member
  /** Public: request a password reset email. Always returns generically. */
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    const response = await apiRequest.post<{ message: string }>('/auth/forgot-password', { email });
    return response.data;
  },

  /** Public: complete a password reset with the emailed token. */
  async resetPassword(
    token: string,
    newPassword: string,
    confirmPassword: string,
  ): Promise<{ message: string }> {
    const response = await apiRequest.post<{ message: string }>('/auth/reset-password', {
      token,
      newPassword,
      confirmPassword,
    });
    return response.data;
  },

  async registerFamily(userData: RegisterFamilyData): Promise<AuthResponse> {
    const response = await apiRequest.post<AuthResponse>('/auth/register/family', userData)
    return response.data
  },

  /** Live referral-code check for the registration form (UX only — the
   *  registration endpoint re-validates authoritatively). */
  async validateReferralCode(code: string): Promise<{ valid: boolean; reason?: string }> {
    const response = await apiRequest.post<{ valid: boolean; reason?: string }>('/referrals/validate', { code })
    return response.data
  },

  /** Whether the referral program is enabled — the registration form hides
   *  its referral field when it is not. */
  async referralStatus(): Promise<{ enabled: boolean }> {
    const response = await apiRequest.get<{ enabled: boolean }>('/referrals/status')
    return response.data
  },

  // Register service provider
  async registerProvider(userData: any): Promise<AuthResponse> {
    const response = await apiRequest.post<AuthResponse>('/auth/register/provider', userData)
    return response.data
  },

  // Register service provider with documents
  async registerProviderWithDocuments(userData: any, documents: Record<string, File>): Promise<AuthResponse> {
    const formData = new FormData()

    // Add all user data as JSON string
    formData.append('userData', JSON.stringify(userData))

    // Add all documents
    Object.entries(documents).forEach(([docType, file]) => {
      if (file) {
        formData.append(docType, file)
      }
    })

    const response = await apiUpload<AuthResponse>('/auth/register/provider-with-documents', formData)
    return response.data
  },

  // Get current user profile
  async getProfile(): Promise<User> {
    const response = await apiRequest.get<User>('/auth/profile')
    return response.data
  },

  // Store auth data in localStorage
  setAuthData(token: string, user: User): void {
    localStorage.setItem('token', token)
    localStorage.setItem('user', JSON.stringify(user))
  },

  // Get stored token
  getToken(): string | null {
    return localStorage.getItem('token')
  },

  // Get stored user
  getStoredUser(): User | null {
    const userStr = localStorage.getItem('user')
    if (!userStr) return null

    const user = JSON.parse(userStr)
    // Ensure backward compatibility by mapping phoneNumber to phone if phone is not present
    return {
      ...user,
      phone: user.phone || user.phoneNumber || undefined,
    }
  },

  // Clear auth data
  clearAuthData(): void {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
  },

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!this.getToken()
  },

  // Update stored user data (used after profile updates)
  updateStoredUser(user: User): void {
    // Ensure backward compatibility by mapping phoneNumber to phone if phone is not present
    const normalizedUser = {
      ...user,
      phone: user.phone || user.phoneNumber || undefined,
    }
    localStorage.setItem('user', JSON.stringify(normalizedUser))
  },
}